import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { getPost, POSTS } from "@/data/blogData";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  useDocumentTitle(
    post ? `${post.title} — Azure Gypsy Guild Case Files` : "Case File — Azure Gypsy Guild",
    post?.metaDescription || "",
  );

  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `https://azuregypsyguild.org/blog/${post.slug}`;
  const ldPost = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.cover,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": { "@type": "Organization", "name": "Azure Gypsy Guild", "url": "https://azuregypsyguild.org/" },
    "publisher": {
      "@type": "Organization",
      "name": "Azure Gypsy Guild",
      "logo": { "@type": "ImageObject", "url": "https://static.prod-images.emergentagent.com/jobs/4e2bdc98-ab8f-417d-b100-db42a3ecfaf3/images/2c4d4f557900b39c29a30c3708dd6bf80de599af925c4589b0748935140e0e0b.png" },
    },
    "mainEntityOfPage": canonical,
    "keywords": post.tags.join(", "),
    "wordCount": post.body.reduce((n, s) => n + s.p.join(" ").split(/\s+/).length, 0),
  };
  const ldBreadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://azuregypsyguild.org/" },
      { "@type": "ListItem", "position": 2, "name": "Case Files", "item": "https://azuregypsyguild.org/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": canonical },
    ],
  };

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-paper" data-testid={`blog-post-${post.slug}`}>
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldPost) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }} />
      <link rel="canonical" href={canonical} />

      <main>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="max-w-[1400px] mx-auto px-6 md:px-12 pt-8">
          <ol className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500 flex-wrap">
            <li><Link to="/" className="hover:text-stone-900">Home</Link></li>
            <li><ChevronRight className="h-3 w-3" /></li>
            <li><Link to="/blog" className="hover:text-stone-900">Case Files</Link></li>
            <li><ChevronRight className="h-3 w-3" /></li>
            <li className="text-stone-900 truncate max-w-[50%]">{post.tags[0]}</li>
          </ol>
        </nav>

        {/* Hero */}
        <article className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 pb-20 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">
            <div className="lg:col-span-9">
              <div className="caption text-stone-500 mb-6 flex items-center gap-3 flex-wrap">
                <span>{new Date(post.date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
                <span className="text-stone-300">·</span>
                <span>{post.author}</span>
                <span className="text-stone-300">·</span>
                <span>{post.readMinutes} min read</span>
              </div>
              <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-stone-900 text-balance">
                {post.title}
              </h1>
              <p className="mt-8 text-lg md:text-xl text-stone-600 font-light leading-relaxed max-w-3xl text-pretty">
                {post.excerpt}
              </p>
              <div className="mt-8 flex items-center gap-2 flex-wrap">
                {post.tags.map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 border border-stone-200 px-2.5 py-1">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Cover image */}
          <figure className="mt-12 border border-stone-200">
            <img
              src={post.cover}
              alt={post.coverAlt}
              className="w-full aspect-[21/9] object-cover grayscale contrast-[1.05]"
              loading="eager"
            />
            <figcaption className="caption text-stone-500 px-4 py-3 border-t border-stone-200 bg-white">
              {post.coverAlt}
            </figcaption>
          </figure>

          {/* Body */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mt-16 md:mt-24">
            <aside className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-32 space-y-3">
                <div className="overline">§ In this file</div>
                {post.body.map((s, i) => (
                  <a
                    key={i}
                    href={`#h${i}`}
                    className="block text-sm text-stone-600 hover:text-stone-900 font-light border-l border-stone-200 hover:border-stone-900 pl-4 py-1 transition-colors"
                  >
                    {s.h}
                  </a>
                ))}
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-14 md:space-y-20">
              {post.body.map((section, i) => (
                <section key={i} id={`h${i}`} className="scroll-mt-32" data-testid={`blog-section-${i}`}>
                  <div className="caption text-stone-400 mb-4">§ {String(i + 1).padStart(2, "0")}</div>
                  <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight text-stone-900 text-balance mb-6">
                    {section.h}
                  </h2>
                  <div className="text-base md:text-lg text-stone-700 font-light leading-relaxed max-w-3xl space-y-5 text-pretty">
                    {section.p.map((para, j) => <p key={j}>{para}</p>)}
                  </div>
                </section>
              ))}
            </div>
          </div>

          {/* Sign-off */}
          <div className="mt-24 pt-10 border-t border-stone-200 flex items-center justify-between flex-wrap gap-6">
            <Link to="/blog" className="btn-ghost" data-testid="blog-back-link">
              <ArrowLeft className="h-4 w-4" /> Back to case files
            </Link>
            <Link to="/register" className="btn-primary" data-testid="blog-cta-register">
              Start my free case review <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="bg-stone-50 border-t border-stone-200 py-20 md:py-28" data-testid="blog-related">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
              <div className="overline mb-6">§ Related Case Files</div>
              <div className="grid md:grid-cols-2 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="border border-stone-200 bg-white p-8 hover:bg-white hover:border-stone-900 transition-colors group"
                    data-testid={`blog-related-${r.slug}`}
                  >
                    <div className="caption text-stone-400 mb-3">
                      {new Date(r.date).toLocaleDateString(undefined, { month: "short", year: "numeric" })} · {r.readMinutes} min
                    </div>
                    <h3 className="font-serif font-light text-2xl leading-tight group-hover:text-stone-600 transition-colors text-balance">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-stone-600 font-light leading-relaxed">
                      {r.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
